import { useCallback, useMemo, useState } from "react";

import { FormProps } from "antd/es/form";
import { differenceInCalendarDays } from "date-fns";

import ActivityVisibility from "@mapmycustomers/shared/enum/activity/ActivityVisibility";
import { Activity } from "@mapmycustomers/shared/types/entity";

import RecurringIntervalType from "@app/enum/RecurringIntervalType";
import { stringifyActivityInterval } from "@app/util/activity/activityInterval";
import { parseApiDate } from "@app/util/parsers";

import FormValues from "../types/FormValues";
import SaveModeStatus from "../types/SaveModeStatus";
import SaveOption from "../types/SaveOption";

const useSaveModeHandler = (
  activity?: Activity
): [SaveModeStatus, NonNullable<FormProps<FormValues>["onValuesChange"]>] => {
  const [options, setOptions] = useState<SaveOption[]>([]);
  const [recurChanged, setRecurChanged] = useState<boolean>(false);
  const [isActivityPublic, setIsActivityPublic] = useState<boolean>(
    activity?.visibility === ActivityVisibility.SHARED_WITH_ORGANIZATION
  );
  const [areAssociationFieldsChanged, setAssociationFieldsChanged] = useState<boolean>(false);
  const [areCheckInFieldsChanged, setCheckInFieldsChanged] = useState(false);

  const isCheckInActivityVerified = useMemo(() => activity?.reliability === true, [activity]);

  const showCheckInSubmit = useMemo(
    () => isCheckInActivityVerified && (areAssociationFieldsChanged || areCheckInFieldsChanged),
    [areAssociationFieldsChanged, areCheckInFieldsChanged, isCheckInActivityVerified]
  );

  const handleChange = useCallback<NonNullable<FormProps<FormValues>["onValuesChange"]>>(
    (_, allFields) => {
      const options = [];
      const isRecurActivity =
        allFields.recurIntervalType &&
        allFields.recurIntervalType !== RecurringIntervalType.NEVER &&
        activity?.recurInterval &&
        activity?.recurInterval !== RecurringIntervalType.NEVER;

      const isRecurIntervalChanged =
        !!allFields.recurInterval &&
        stringifyActivityInterval(allFields.recurInterval) !== activity?.recurInterval;

      let startAtDateIsChanged = false;
      let differenceStartAtInDays = 0;
      if (allFields.startAt) {
        startAtDateIsChanged = true;
        if (activity?.startAt) {
          const parsedActivityStartAt = parseApiDate(activity.startAt);
          differenceStartAtInDays = differenceInCalendarDays(
            allFields.startAt,
            parsedActivityStartAt
          );
          startAtDateIsChanged = allFields.startAt?.getTime() !== parsedActivityStartAt?.getTime();
        }
      }

      const differenceStartAtInDaysMoreThan1Day = differenceStartAtInDays > 1;
      if (isRecurActivity) {
        if (differenceStartAtInDaysMoreThan1Day || isRecurIntervalChanged) {
          if (differenceStartAtInDaysMoreThan1Day && !isRecurIntervalChanged) {
            // When the start time is moved by a day or more than a day.
            options.push(SaveOption.JUST_THIS_ACTIVITY, SaveOption.THIS_AND_FEATURE_ACTIVITIES);
          } else if (!differenceStartAtInDaysMoreThan1Day && isRecurIntervalChanged) {
            // When the recurring interval is changed.
            options.push(SaveOption.ALL_ACTIVITIES, SaveOption.THIS_AND_FEATURE_ACTIVITIES);
          } else {
            options.push(SaveOption.THIS_AND_FEATURE_ACTIVITIES);
          }
        } else if (
          !startAtDateIsChanged ||
          (startAtDateIsChanged && differenceStartAtInDays === 0)
        ) {
          // When only time is changed in star time and not the date
          // OR
          // When any other field is changed
          options.push(
            SaveOption.JUST_THIS_ACTIVITY,
            SaveOption.ALL_ACTIVITIES,
            SaveOption.THIS_AND_FEATURE_ACTIVITIES
          );
        }
      }

      setRecurChanged(differenceStartAtInDaysMoreThan1Day && isRecurIntervalChanged);
      setOptions(options);
      setIsActivityPublic(allFields.visibility === ActivityVisibility.SHARED_WITH_ORGANIZATION);
      setAssociationFieldsChanged(
        (!!activity?.account?.id && activity?.account?.id !== allFields.companyId) ||
          (!!activity?.contact?.id && activity?.contact?.id !== allFields.peopleIds?.[0]) ||
          (!!activity?.deal?.id && activity?.deal?.id !== allFields.dealId)
      );
      const isCompletedChanged = allFields.completed !== activity?.completed;
      const isEndAtChanged =
        //activity?.endAt can be null so need !=
        allFields.endAt?.toISOString() != activity?.endAt; // eslint-disable-line eqeqeq
      const isStartAtChanged = allFields.startAt?.toISOString() !== activity?.startAt;
      setCheckInFieldsChanged(isCompletedChanged || isStartAtChanged || isEndAtChanged);
    },
    [activity]
  );
  return [{ isActivityPublic, options, recurChanged, showCheckInSubmit }, handleChange];
};

export default useSaveModeHandler;
